/*
 * Painel de desempenho 3D do CMA: métricas de renderização (FPS, draw calls,
 * triângulos, memória de GPU/JS) e de carregamento do `.glb` ativo (tempo,
 * tamanho, contagem de triângulos), para comparar um modelo com outro de
 * forma justa — ver `CmaAsset3dView.vue` (animate/refreshPerfHud).
 *
 * Fica fora do <template>/reatividade do Vue de propósito, no mesmo padrão
 * do painel de debug do SPS (ver `buildSpsPanel`, `CmaAsset3dView.vue`):
 * `.cma-dashboard__card-column` (CmaDashboard.vue) tem z-index maior que o
 * container deste componente, então só escapando para um elemento anexado
 * direto em `document.body` o painel sempre renderiza por cima dos cards.
 * A atualização também é DOM direto (`textContent`), não `data` reativo do
 * Vue: a 60fps, o overhead do sistema reativo distorceria a própria medição
 * que o painel existe para mostrar.
 */
// Stub inerte para quando o documento não tem `<body>` no momento do mount
// (ver criarPainelDesempenho) — nunca acontece num navegador real, mas
// acontece nos testes de CmaAsset3dView.vue: o harness monta o componente
// com `attachTo: document.body`, e a própria tag `<body>` é substituída pela
// raiz do componente durante o patch, então `document.body` já é `null`
// quando `mounted()` roda. Devolver um no-op evita quebrar o mount por causa
// de um painel de diagnóstico que não tem onde se pendurar.
const PAINEL_INERTE = { atualizar() {}, remover() {} };

export function criarPainelDesempenho() {
  if (!document.body) return PAINEL_INERTE;

  const panel = document.createElement('div');
  panel.className = 'cma-perf-hud';
  // Bloqueia o evento na origem, antes que o OrbitControls (preso ao canvas)
  // o veja — interagir com o painel não pode também girar/panoramizar a
  // câmera por baixo dele.
  ['pointerdown', 'mousedown', 'wheel'].forEach((tipo) => {
    panel.addEventListener(tipo, evento => evento.stopPropagation());
  });

  const header = document.createElement('div');
  header.className = 'cma-perf-hud__header';

  const titulo = document.createElement('strong');
  titulo.textContent = 'Desempenho 3D';
  header.appendChild(titulo);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'cma-perf-hud__toggle';
  toggle.textContent = '–';
  toggle.setAttribute('aria-label', 'Recolher painel de desempenho');
  header.appendChild(toggle);

  const body = document.createElement('pre');
  body.className = 'cma-perf-hud__body';

  panel.appendChild(header);
  panel.appendChild(body);

  toggle.addEventListener('click', () => {
    const collapsed = panel.classList.toggle('cma-perf-hud--collapsed');
    toggle.textContent = collapsed ? '+' : '–';
    toggle.setAttribute('aria-label', collapsed
      ? 'Expandir painel de desempenho'
      : 'Recolher painel de desempenho');
  });

  document.body.appendChild(panel);

  return {
    /** Substitui o conteúdo do corpo do painel, uma linha por item. */
    atualizar(linhas) {
      body.textContent = linhas.join('\n');
    },
    remover() {
      panel.remove();
    },
  };
}

export default criarPainelDesempenho;
